import { useMutation, useQueryClient } from '@tanstack/react-query'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'

import { api } from '../lib/api'
import { useMe, useSettings } from './RequireAuth'

const NAV = [
  { to: '/', label: 'Overview', end: true },
  { to: '/enquiries', label: 'Enquiries', end: false },
  { to: '/calendar', label: 'Calendar', end: false },
  { to: '/clients', label: 'Clients', end: false },
  { to: '/invoices', label: 'Invoices', end: false },
]

const NAV_BUILD = [
  { to: '/automation', label: 'Automation' },
  { to: '/lab', label: 'Lab' },
  { to: '/team', label: 'Team' },
]

export function Layout() {
  const { data: me } = useMe()
  const settings = useSettings()
  const queryClient = useQueryClient()
  // Below 720px the sidebar collapses into a drawer behind this toggle. It closes on
  // navigation: a menu left open over the page it just took you to is the classic
  // phone-nav annoyance, and Luis will be using this one-handed in the live room.
  const [menu, setMenu] = useState(false)
  const { pathname } = useLocation()
  useEffect(() => setMenu(false), [pathname])

  const signOut = useMutation({
    mutationFn: () => api.post('/auth/logout'),
    onSuccess: () => {
      queryClient.clear()
      window.location.href = '/login'
    },
  })

  return (
    <>
      <aside className={`sidebar ${menu ? 'open' : ''}`}>
        <div className="side-top">
          <NavLink to="/" className="logo">
            Old Street
            <br />
            Studios <span>Ops</span>
          </NavLink>
          <button
            type="button"
            className="navtoggle"
            aria-expanded={menu}
            aria-label={menu ? 'Close menu' : 'Open menu'}
            onClick={() => setMenu((open) => !open)}
          >
            {menu ? '✕' : '☰'}
          </button>
        </div>

        <div className="side-body">
          <nav>
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) => `nav-item ${isActive ? 'on' : ''}`}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="nav-group">Building</div>
          <nav>
            {NAV_BUILD.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) => `nav-item ${isActive ? 'on' : ''}`}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="side-foot">
            <div className="rate"><b>Music</b> £60/hr · £420/day</div>
            <div className="rate"><b>Voice &amp; podcast</b> £80/hr · £560/day</div>
            <div className="rate"><b>Mastering</b> £90/track</div>
            {me && (
              <div className="whoami">
                <div className="whoami-name">{me.name}</div>
                <div className="whoami-role">{me.role}</div>
                <button type="button" className="signout" onClick={() => signOut.mutate()}>
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>
      <main>
        {settings.data?.open_access && (
          <div className="openbanner">
            <b>Open access</b> — no sign-in required. This instance is empty and not wired to
            anything. Authentication switches itself back on the moment real enquiries are
            imported.
          </div>
        )}
        <Outlet />
      </main>
    </>
  )
}

export function PageBar({ title, actions }: { title: ReactNode; actions?: ReactNode }) {
  return (
    <header className="pagebar">
      <h1>{title}</h1>
      {actions && <div className="pagebar-actions">{actions}</div>}
    </header>
  )
}

export function Content({ children }: { children: ReactNode }) {
  return <div className="content">{children}</div>
}
